import type { ToolDetectedFormat, ToolErrorCode } from '@saas-pages/shared/public/runtime'

export type ScanInputMethod = 'upload' | 'camera'
export type ScannerMode = 'qr' | 'barcode'
export type SupportedBarcodeFormat = 'EAN-13' | 'EAN-8' | 'UPC-A' | 'Code 128'

export interface DecodedCandidate {
  text: string
  format: SupportedBarcodeFormat
}

export type ScanErrorCode =
  | 'no_qr_found'
  | 'multiple_codes'
  | 'unsupported_file'
  | 'file_too_large'
  | 'scanner_failed'
  | 'permission_denied'
  | 'unsupported_browser'
  | 'camera_failed'
  | 'camera_timeout'
  | 'clipboard_empty'
  | 'clipboard_unavailable'

export interface ScanResult {
  text: string
  inputMethod: ScanInputMethod
  format?: SupportedBarcodeFormat
}

export interface DecodeResult {
  kind: 'none' | 'single' | 'multiple'
  text?: string
  format?: SupportedBarcodeFormat
  candidates?: DecodedCandidate[]
}

export const MAX_FILE_BYTES = 20 * 1024 * 1024
export const MAX_IMAGE_DIMENSION = 12_000
export const MAX_IMAGE_PIXELS = 40_000_000
export const MAX_STRUCTURE_BYTES = 256 * 1024
export const SUPPORTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

export function classifyDecodedTexts(texts: readonly string[]): DecodeResult {
  if (texts.length === 0) return { kind: 'none' }
  if (texts.length > 1) return { kind: 'multiple' }
  return { kind: 'single', text: texts[0] ?? '' }
}

export function classifyBarcodeCandidates(
  candidates: readonly DecodedCandidate[],
): DecodeResult {
  if (candidates.length === 0) return { kind: 'none' }
  if (candidates.length > 1) return { kind: 'multiple', candidates: [...candidates] }
  const candidate = candidates[0]
  return {
    kind: 'single',
    text: candidate?.text ?? '',
    format: candidate?.format,
  }
}

export function normalizeBarcodeCandidate(
  text: string,
  format: string,
): DecodedCandidate | null {
  if (format === 'EAN13' && /^0\d{12}$/.test(text)) {
    return { text: text.slice(1), format: 'UPC-A' }
  }
  if (format === 'EAN13') return { text, format: 'EAN-13' }
  if (format === 'EAN8') return { text, format: 'EAN-8' }
  if (format === 'UPCA') return { text, format: 'UPC-A' }
  if (format === 'Code128') return { text, format: 'Code 128' }
  return null
}

export function barcodeAnalyticsFormat(format: SupportedBarcodeFormat): ToolDetectedFormat {
  if (format === 'EAN-13') return 'ean-13'
  if (format === 'EAN-8') return 'ean-8'
  if (format === 'UPC-A') return 'upc-a'
  return 'code-128'
}

export function validateImageFile(file: Pick<File, 'size' | 'type'>): ScanErrorCode | null {
  if (file.size > MAX_FILE_BYTES) return 'file_too_large'
  if (!SUPPORTED_IMAGE_TYPES.has(file.type.toLowerCase())) return 'unsupported_file'
  return null
}

export async function inspectImageStructure(file: File): Promise<ScanErrorCode | null> {
  const bytes = new Uint8Array(await file.slice(0, MAX_STRUCTURE_BYTES).arrayBuffer())
  const dimensions = parseImageDimensions(bytes, file.type)
  if (!dimensions) return 'unsupported_file'
  if (
    dimensions.width > MAX_IMAGE_DIMENSION
    || dimensions.height > MAX_IMAGE_DIMENSION
    || dimensions.width * dimensions.height > MAX_IMAGE_PIXELS
  ) return 'file_too_large'
  return null
}

export function parseImageDimensions(
  bytes: Uint8Array,
  type: string,
): { width: number; height: number } | null {
  const dimensions = type === 'image/png'
    ? parsePngDimensions(bytes)
    : type === 'image/jpeg'
      ? parseJpegDimensions(bytes)
      : type === 'image/webp'
        ? parseWebpDimensions(bytes)
        : null
  if (!dimensions || dimensions.width <= 0 || dimensions.height <= 0) return null
  return dimensions
}

function parsePngDimensions(bytes: Uint8Array) {
  if (
    bytes.length < 24
    || bytes[0] !== 0x89
    || ascii(bytes, 1, 3) !== 'PNG'
    || bytes[4] !== 0x0d
    || bytes[5] !== 0x0a
    || bytes[6] !== 0x1a
    || bytes[7] !== 0x0a
    || ascii(bytes, 12, 4) !== 'IHDR'
  ) return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  return { width: view.getUint32(16), height: view.getUint32(20) }
}

function parseJpegDimensions(bytes: Uint8Array) {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  let offset = 2
  while (offset + 4 <= bytes.length) {
    while (bytes[offset] === 0xff) offset += 1
    const marker = bytes[offset]
    offset += 1
    if (marker === undefined || marker === 0xd9 || marker === 0xda) return null
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue
    if (offset + 2 > bytes.length) return null
    const segmentLength = view.getUint16(offset)
    if (segmentLength < 2 || offset + segmentLength > bytes.length) return null
    if (isJpegStartOfFrame(marker)) {
      if (segmentLength < 7) return null
      return { width: view.getUint16(offset + 5), height: view.getUint16(offset + 3) }
    }
    offset += segmentLength
  }
  return null
}

function isJpegStartOfFrame(marker: number): boolean {
  return marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker)
}

function parseWebpDimensions(bytes: Uint8Array) {
  if (bytes.length < 30 || ascii(bytes, 0, 4) !== 'RIFF' || ascii(bytes, 8, 4) !== 'WEBP') return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  let offset = 12
  while (offset + 8 <= bytes.length) {
    const chunk = ascii(bytes, offset, 4)
    const chunkLength = view.getUint32(offset + 4, true)
    const dataOffset = offset + 8
    if (dataOffset + Math.min(chunkLength, 10) > bytes.length) return null
    if (chunk === 'VP8X' && chunkLength >= 10) {
      return {
        width: 1 + uint24(bytes, dataOffset + 4),
        height: 1 + uint24(bytes, dataOffset + 7),
      }
    }
    if (chunk === 'VP8L' && chunkLength >= 5 && bytes[dataOffset] === 0x2f) {
      const b1 = bytes[dataOffset + 1] ?? 0
      const b2 = bytes[dataOffset + 2] ?? 0
      const b3 = bytes[dataOffset + 3] ?? 0
      const b4 = bytes[dataOffset + 4] ?? 0
      return { width: 1 + b1 + ((b2 & 0x3f) << 8), height: 1 + (b2 >> 6) + (b3 << 2) + ((b4 & 0x0f) << 10) }
    }
    if (chunk === 'VP8 ' && chunkLength >= 10 && bytes[dataOffset + 3] === 0x9d && bytes[dataOffset + 4] === 0x01 && bytes[dataOffset + 5] === 0x2a) {
      return { width: view.getUint16(dataOffset + 6, true) & 0x3fff, height: view.getUint16(dataOffset + 8, true) & 0x3fff }
    }
    offset = dataOffset + chunkLength + (chunkLength % 2)
  }
  return null
}

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(offset, offset + length))
}

function uint24(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0) | ((bytes[offset + 1] ?? 0) << 8) | ((bytes[offset + 2] ?? 0) << 16)
}

export function analyticsErrorFor(code: ScanErrorCode): ToolErrorCode {
  if (
    code === 'unsupported_file'
    || code === 'unsupported_browser'
    || code === 'clipboard_empty'
    || code === 'clipboard_unavailable'
  ) return 'unsupported_input'
  if (code === 'file_too_large') return 'size_limit'
  if (code === 'camera_timeout') return 'timeout'
  return 'processing_failed'
}

export function parseSafeUrl(value: string): URL | null {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null
  } catch {
    return null
  }
}
